const DB_NAME = 'pubinvest-assessment-drafts';
const DB_VERSION = 2;
const STORE_NAME = 'operations';
const OPERATION_INDEX = 'operationId';
const SCOPE_INDEX = 'scope';
const SCOPE_OPERATION_INDEX = 'scopeOperation';
const DEFAULT_SCOPE = 'default';

function clone(value) {
  return value === null || value === undefined
    ? value
    : structuredClone(value);
}

function sanitizeOperation(operation) {
  return {
    operationId: operation.operationId,
    schemaVersion: operation.schemaVersion,
    currentSection: operation.currentSection,
    updates: clone(operation.updates),
  };
}

function normalizeScope(scope) {
  return typeof scope === 'string' && scope.length > 0 ? scope : DEFAULT_SCOPE;
}

function scopedRecord(scope, operation) {
  return {
    ...sanitizeOperation(operation),
    scope,
  };
}

function requestPromise(request) {
  return new Promise((resolve, reject) => {
    request.addEventListener('success', () => resolve(request.result), { once: true });
    request.addEventListener('error', () => reject(request.error), { once: true });
  });
}

function transactionPromise(transaction) {
  return new Promise((resolve, reject) => {
    transaction.addEventListener('complete', resolve, { once: true });
    transaction.addEventListener('abort', () => reject(transaction.error), { once: true });
    transaction.addEventListener('error', () => reject(transaction.error), { once: true });
  });
}

function cursorPromise(request, callback) {
  return new Promise((resolve, reject) => {
    request.addEventListener('success', () => {
      const cursor = request.result;
      if (!cursor) {
        resolve();
        return;
      }

      callback(cursor);
      cursor.continue();
    });
    request.addEventListener('error', () => reject(request.error), { once: true });
  });
}

function ensureIndexes(store) {
  if (store.indexNames.contains(OPERATION_INDEX)) {
    store.deleteIndex(OPERATION_INDEX);
  }
  if (!store.indexNames.contains(SCOPE_INDEX)) {
    store.createIndex(SCOPE_INDEX, SCOPE_INDEX, { unique: false });
  }
  if (!store.indexNames.contains(SCOPE_OPERATION_INDEX)) {
    store.createIndex(SCOPE_OPERATION_INDEX, [SCOPE_INDEX, OPERATION_INDEX], { unique: true });
  }
}

function openDatabase(indexedDB, databaseName) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, DB_VERSION);
    request.addEventListener('upgradeneeded', () => {
      const database = request.result;
      const store = database.objectStoreNames.contains(STORE_NAME)
        ? request.transaction.objectStore(STORE_NAME)
        : database.createObjectStore(STORE_NAME, { autoIncrement: true });
      ensureIndexes(store);
    });
    request.addEventListener('success', () => resolve(request.result), { once: true });
    request.addEventListener('error', () => reject(request.error), { once: true });
    request.addEventListener('blocked', () => reject(new Error('IndexedDB upgrade blocked')), { once: true });
  });
}

function memoryStorage() {
  const operationsByScope = new Map();

  function operationsFor(scope) {
    if (!operationsByScope.has(scope)) operationsByScope.set(scope, []);
    return operationsByScope.get(scope);
  }

  return {
    async enqueue(scope, operation) {
      const operations = operationsFor(scope);
      const sanitized = sanitizeOperation(operation);
      const index = operations.findIndex((item) => item.operationId === sanitized.operationId);
      if (index >= 0) operations[index] = sanitized;
      else operations.push(sanitized);
    },
    async pending(scope) {
      return clone(operationsFor(scope));
    },
    async acknowledge(scope, operationId) {
      const operations = operationsFor(scope);
      const index = operations.findIndex((item) => item.operationId === operationId);
      if (index >= 0) operations.splice(index, 1);
    },
    async clear(scope) {
      operationsFor(scope).length = 0;
    },
  };
}

function indexedDbStorage(database) {
  return {
    async enqueue(scope, operation) {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index(SCOPE_OPERATION_INDEX);
      const record = scopedRecord(scope, operation);
      const existingKey = await requestPromise(index.getKey([scope, record.operationId]));
      if (existingKey === undefined) store.add(record);
      else store.put(record, existingKey);
      await transactionPromise(transaction);
    },
    async pending(scope) {
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const operations = await requestPromise(
        transaction.objectStore(STORE_NAME).index(SCOPE_INDEX).getAll(scope),
      );
      await transactionPromise(transaction);
      return clone(operations.map(sanitizeOperation));
    },
    async acknowledge(scope, operationId) {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const key = await requestPromise(store.index(SCOPE_OPERATION_INDEX).getKey([scope, operationId]));
      if (key !== undefined) store.delete(key);
      await transactionPromise(transaction);
    },
    async clear(scope) {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      await cursorPromise(
        store.index(SCOPE_INDEX).openKeyCursor(IDBKeyRange.only(scope)),
        (cursor) => store.delete(cursor.primaryKey),
      );
      await transactionPromise(transaction);
    },
  };
}

export function createDraftQueue({
  adapter = null,
  indexedDB = globalThis.indexedDB,
  databaseName = DB_NAME,
  persistent,
  scope: requestedScope,
} = {}) {
  const scope = normalizeScope(requestedScope);
  let persistentStorage = adapter ? Boolean(persistent ?? true) : Boolean(persistent ?? indexedDB);
  const fallback = memoryStorage();
  const storagePromise = adapter
    ? Promise.resolve(adapter)
    : indexedDB
      ? openDatabase(indexedDB, databaseName).then(indexedDbStorage).catch(() => {
        persistentStorage = false;
        return fallback;
      })
      : Promise.resolve(fallback);

  async function storage() {
    try {
      return await storagePromise;
    } catch {
      persistentStorage = false;
      return fallback;
    }
  }

  return {
    async enqueue(operation) {
      return (await storage()).enqueue(scope, sanitizeOperation(operation));
    },
    async pending() {
      return (await storage()).pending(scope);
    },
    async acknowledge(operationId) {
      return (await storage()).acknowledge(scope, operationId);
    },
    async clear() {
      return (await storage()).clear(scope);
    },
    async overlay(draft = {}) {
      const operations = await this.pending();
      const restored = {
        ...draft,
        answers: clone(draft.answers ?? {}),
        currentSection: draft.currentSection || 'introduction',
      };
      for (const operation of operations) {
        restored.answers = { ...restored.answers, ...clone(operation.updates) };
        restored.currentSection = operation.currentSection || restored.currentSection;
      }
      return restored;
    },
    async isPersistent() {
      await storage();
      return persistentStorage;
    },
  };
}
